import type { ContentItem, Menu, Page, Reference } from '@ps/web-martech-cms-types';
import { useResolver } from '@ps/web-shared-components';
import type { SportsContentItem } from '@ps/web-sports-page-types';
import Graph from 'graph-data-structure';
import { get } from 'lodash';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { useCMSReference } from '@swf/cms';
import { getState, select } from '@swf/core/store';

export const CMSMenuGraphContext = React.createContext(Graph());

interface Props {
  menu: Menu;
  maxDepth?: number;
}

type GraphType = ReturnType<typeof Graph>;

const addMenuGraphItem = (
  reference: Reference,
  graph: GraphType,
  rootNode: string,
  previousNode: string,
  content: Record<string, ContentItem | SportsContentItem>
) => {
  const {
    page: pageRef,
    url,
    resolver_tag: resolverTag,
    children: submenu
  } = (get(content, reference?.uid || '') as Menu | undefined) || {};
  const page = get(content, pageRef?.[0]?.uid || '') as Page | undefined;
  const resolverLink = select.resolver.getTag(getState(), resolverTag || '');
  const href = url || resolverLink?.url || page?.url;

  if (href && !graph.shortestPath(rootNode, previousNode).includes(href)) {
    graph.addEdge(previousNode, href);
    submenu?.forEach((item) => addMenuGraphItem(item, graph, rootNode, href, content));
  }
};

const CMSMenuGraphProvider: React.FC<React.PropsWithChildren<Props>> = ({ menu, children }) => {
  const content = useSelector(select.cms.getContent);
  const graphRef = useRef(Graph());
  const { page: pageRef, url, children: submenu, resolver_tag: resolverTag } = menu;
  const page = useCMSReference<Page>(pageRef?.[0]);
  const resolverLink = useResolver(resolverTag);
  const href = url || resolverLink || page?.url || '/';
  const graphNodes = graphRef.current.nodes();
  if (graphNodes.length > 0 && href !== graphNodes[0]) {
    graphRef.current = Graph();
  }

  graphRef.current.addNode(href);

  submenu.forEach((reference) =>
    addMenuGraphItem(reference, graphRef.current, href, href, content)
  );

  return (
    <CMSMenuGraphContext.Provider value={graphRef.current}>{children}</CMSMenuGraphContext.Provider>
  );
};

export default CMSMenuGraphProvider;
